import { deleteToken, getToken, type Messaging } from 'firebase/messaging';
import { initializeMessaging } from './config';
import { browser } from '$app/environment';
import { PUBLIC_ENV, PUBLIC_WALLET_SERVER } from '$env/static/public';
import { requestNotificationPermission } from '$utils/pushNotifications';
import type {
  AddressSubscription,
  NotificationPreferences,
  SubscriptionResponse,
} from '$types/pushNotifications';
import { getStoredToken, storeToken } from './tokenLifecycle';

const FCM_BACKEND_URL = `${PUBLIC_WALLET_SERVER}/push-notifications`;
const VAPID_KEY =
  PUBLIC_ENV === 'production'
    ? 'BAMzvEThGGEBm6d03LNHzTMMhr1bF6PVfWLMxRWIYop7c8f_Uz4_3zumrqhIEw2exS8gVeKNWs5Sbf0tEtdPNKM'
    : 'BCzfwUzteRQ21a9ZQ7ZrrWOxib8AhSeUfqzqiEB4zDltw-u4G16p9A3GI-NXmeHQYwzGIe9TV7SIkaRFNdn1ijA';

let messaging: Messaging | null = null;

const getServiceWorkerRegistration = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!browser || !('serviceWorker' in navigator)) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    return registration;
  } catch (_) {
    return null;
  }
};

export const initializeFCM = async (): Promise<Messaging | null> => {
  if (!browser) {
    return null;
  }

  try {
    messaging = await initializeMessaging();
    return messaging;
  } catch (_error) {
    const { error } = console;
    error('💥 Failed to initialize FCM:', _error);
    return null;
  }
};

export const getFCMToken = async (): Promise<string | null> => {
  if (!messaging) {
    messaging = await initializeFCM();
  }

  if (!messaging) {
    return null;
  }

  try {
    const permission = await requestNotificationPermission();

    if (permission !== 'granted') {
      return null;
    }

    const serviceWorkerRegistration = await getServiceWorkerRegistration();

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: serviceWorkerRegistration || undefined,
    });

    return token;
  } catch (_error) {
    const { error } = console;
    error('💥 Error getting FCM token:', _error);
    return null;
  }
};

export const subscribeFCM = async (
  address: string,
  preferences: NotificationPreferences,
  retryCount = 0,
): Promise<boolean> => {
  const token = getStoredToken();
  if (!token) {
    throw new Error('Failed to get FCM token');
  }

  const response = await fetch(`${FCM_BACKEND_URL}/association`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      address,
      fcmToken: token,
      preferences,
    }),
  });

  if (!response.ok) {
    if (response.status === 400) {
      const errorData = await response.json();

      if (errorData.code === 'INVALID_TOKEN' && retryCount === 0) {
        await deleteFCMToken();
        const newToken = await getFCMToken();
        if (newToken) {
          storeToken(newToken);
          return subscribeFCM(address, preferences, retryCount + 1);
        }
      }

      throw new Error(`Failed to update notifications: ${errorData.error}`);
    } else {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  }

  return true;
};

export const unsubscribeFCM = async (address: string): Promise<boolean> => {
  const response = await fetch(`${FCM_BACKEND_URL}/association`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      address,
    }),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return true;
};

export const checkSubscriptionStatus = async (
  address: string,
): Promise<SubscriptionResponse | null> => {
  const response = await fetch(`${FCM_BACKEND_URL}/subscriptions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ address }),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const data: SubscriptionResponse = await response.json();
  return data.success ? data : null;
};

export const deleteFCMToken = async (): Promise<void> => {
  if (!messaging) {
    return;
  }

  await deleteToken(messaging);
};

export const refreshFCMToken = async (subscriptions: AddressSubscription[]): Promise<void> => {
  try {
    await deleteFCMToken();

    for (const { address, preferences } of subscriptions) {
      await subscribeFCM(address, preferences, 0);
    }
  } catch (_error) {
    const { error } = console;
    error('💥 Error refreshing FCM token:', _error);
    throw _error;
  }
};
